/**
 * Local Data Service - Works without AWS services
 * Uses IndexedDB directly for offline storage
 * Perfect for local testing without AWS setup
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Local model interfaces
export interface LocalDataItem {
  id: string;
  name: string;
  description?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
}

export interface LocalUser {
  id: string;
  name: string;
  email: string;
  role?: string;
  status?: string;
  lastLogin?: string;
  createdAt?: string;
}

export interface LocalReport {
  id: string;
  title: string;
  type?: string;
  generatedAt?: string;
  status?: string;
  data?: string;
}

export interface LocalAnalyticsData {
  id: string;
  metric: string;
  value?: number;
  timestamp?: string;
  category?: string;
}

export interface LocalActivityLog {
  id: string;
  description: string;
  timestamp?: string;
  userId?: string;
  action?: string;
}

export interface LocalSafetyForm {
  id: string;
  formType?: string;
  requiredPermits?: string[];
  requiredChecklist?: string[];
  ppe?: string;
  hazards?: string[];
  controlMeasures?: string[];
  tasks?: string[];
  emergencyActionPlan?: boolean;
  emergencyMusterArea?: string;
  emergencyContacts?: string;
  teamMembers?: string[];
  supervisorStartSignature?: string;
  supervisorStartDate?: string;
  supervisorStartPhone?: string;
  endOfShiftReview?: string;
  supervisorEndSignature?: string;
  supervisorEndDate?: string;
  supervisorEndPhone?: string;
  superintendentEndPhone?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  // PTP-specific fields
  foremanName?: string;
  foremanSignature?: string;
  crewSignatures?: Array<{ name: string; signature: string; timestamp: string }> | string;
  superintendentSignature?: string;
  superintendentReviewDate?: string;
  nobodyHurtAcknowledgment?: boolean;
  nobodyHurtDate?: string;
  projectName?: string;
  location?: string;
  workDate?: string;
}

interface LocalDB extends DBSchema {
  dataItems: {
    key: string;
    value: LocalDataItem;
    indexes: { 'by-status': string; 'by-name': string };
  };
  users: {
    key: string;
    value: LocalUser;
    indexes: { 'by-email': string; 'by-role': string };
  };
  reports: {
    key: string;
    value: LocalReport;
    indexes: { 'by-type': string; 'by-status': string };
  };
  analytics: {
    key: string;
    value: LocalAnalyticsData;
    indexes: { 'by-metric': string; 'by-category': string };
  };
  activities: {
    key: string;
    value: LocalActivityLog;
    indexes: { 'by-timestamp': string };
  };
  safetyForms: {
    key: string;
    value: LocalSafetyForm;
    indexes: { 'by-status': string; 'by-formType': string };
  };
}

let db: IDBPDatabase<LocalDB> | null = null;

export async function initLocalDB(): Promise<IDBPDatabase<LocalDB>> {
  if (db) return db;

  db = await openDB<LocalDB>('LocalOfflineDB', 2, {
    upgrade(db, _oldVersion, _newVersion, _transaction) {
      // Check and create object stores only if they don't exist
      // This prevents "object store already exists" errors
      
      // DataItems store
      if (!db.objectStoreNames.contains('dataItems')) {
        const dataItemsStore = db.createObjectStore('dataItems', { keyPath: 'id' });
        dataItemsStore.createIndex('by-status', 'status');
        dataItemsStore.createIndex('by-name', 'name');
      }

      // Users store
      if (!db.objectStoreNames.contains('users')) {
        const usersStore = db.createObjectStore('users', { keyPath: 'id' });
        usersStore.createIndex('by-email', 'email');
        usersStore.createIndex('by-role', 'role');
      }

      // Reports store
      if (!db.objectStoreNames.contains('reports')) {
        const reportsStore = db.createObjectStore('reports', { keyPath: 'id' });
        reportsStore.createIndex('by-type', 'type');
        reportsStore.createIndex('by-status', 'status');
      }

      // Analytics store
      if (!db.objectStoreNames.contains('analytics')) {
        const analyticsStore = db.createObjectStore('analytics', { keyPath: 'id' });
        analyticsStore.createIndex('by-metric', 'metric');
        analyticsStore.createIndex('by-category', 'category');
      }

      // Activities store
      if (!db.objectStoreNames.contains('activities')) {
        const activitiesStore = db.createObjectStore('activities', { keyPath: 'id' });
        activitiesStore.createIndex('by-timestamp', 'timestamp');
      }

      // Safety Forms store
      if (!db.objectStoreNames.contains('safetyForms')) {
        const safetyFormsStore = db.createObjectStore('safetyForms', { keyPath: 'id' });
        safetyFormsStore.createIndex('by-status', 'status');
        safetyFormsStore.createIndex('by-formType', 'formType');
      }
    },
  });

  return db;
}

// DataItem Service
export const localDataItemService = {
  async getAll(): Promise<LocalDataItem[]> {
    const database = await initLocalDB();
    return await database.getAll('dataItems');
  },

  async getById(id: string): Promise<LocalDataItem | undefined> {
    const database = await initLocalDB();
    return await database.get('dataItems', id);
  },

  async create(item: Partial<LocalDataItem>): Promise<LocalDataItem> {
    const database = await initLocalDB();
    const newItem: LocalDataItem = {
      id: crypto.randomUUID(),
      name: item.name || '',
      description: item.description || '',
      status: item.status || 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
      ...item,
    };
    await database.put('dataItems', newItem);
    return newItem;
  },

  async update(id: string, updates: Partial<LocalDataItem>): Promise<LocalDataItem> {
    const database = await initLocalDB();
    const existing = await database.get('dataItems', id);
    if (!existing) throw new Error('Item not found');
    const updated: LocalDataItem = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
      version: (existing.version || 1) + 1,
    };
    await database.put('dataItems', updated);
    return updated;
  },

  async delete(id: string): Promise<void> {
    const database = await initLocalDB();
    await database.delete('dataItems', id);
  },
};

// User Service
export const localUserService = {
  async getAll(): Promise<LocalUser[]> {
    const database = await initLocalDB();
    return await database.getAll('users');
  },

  async getById(id: string): Promise<LocalUser | undefined> {
    const database = await initLocalDB();
    return await database.get('users', id);
  },

  async create(user: Partial<LocalUser>): Promise<LocalUser> {
    const database = await initLocalDB();
    const newUser: LocalUser = {
      id: crypto.randomUUID(),
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'user',
      status: user.status || 'active',
      lastLogin: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      ...user,
    };
    await database.put('users', newUser);
    return newUser;
  },

  async update(id: string, updates: Partial<LocalUser>): Promise<LocalUser> {
    const database = await initLocalDB();
    const existing = await database.get('users', id);
    if (!existing) throw new Error('User not found');
    const updated: LocalUser = {
      ...existing,
      ...updates,
    };
    await database.put('users', updated);
    return updated;
  },

  async delete(id: string): Promise<void> {
    const database = await initLocalDB();
    await database.delete('users', id);
  },
};

// Report Service
export const localReportService = {
  async getAll(): Promise<LocalReport[]> {
    const database = await initLocalDB();
    return await database.getAll('reports');
  },

  async create(report: Partial<LocalReport>): Promise<LocalReport> {
    const database = await initLocalDB();
    const newReport: LocalReport = {
      id: crypto.randomUUID(),
      title: report.title || '',
      type: report.type || '',
      generatedAt: new Date().toISOString(),
      status: report.status || 'pending',
      data: report.data || '{}',
      ...report,
    };
    await database.put('reports', newReport);
    return newReport;
  },
};

// Analytics Service
export const localAnalyticsService = {
  async getAll(): Promise<LocalAnalyticsData[]> {
    const database = await initLocalDB();
    return await database.getAll('analytics');
  },

  async create(analytics: Partial<LocalAnalyticsData>): Promise<LocalAnalyticsData> {
    const database = await initLocalDB();
    const newAnalytics: LocalAnalyticsData = {
      id: crypto.randomUUID(),
      metric: analytics.metric || '',
      value: analytics.value || 0,
      timestamp: new Date().toISOString(),
      category: analytics.category || '',
      ...analytics,
    };
    await database.put('analytics', newAnalytics);
    return newAnalytics;
  },
};

// Activity Service
export const localActivityService = {
  async getRecent(limit: number = 20): Promise<LocalActivityLog[]> {
    const database = await initLocalDB();
    const all = await database.getAll('activities');
    return all
      .sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      })
      .slice(0, limit);
  },

  async create(activity: Partial<LocalActivityLog>): Promise<LocalActivityLog> {
    const database = await initLocalDB();
    const newActivity: LocalActivityLog = {
      id: crypto.randomUUID(),
      description: activity.description || '',
      timestamp: new Date().toISOString(),
      userId: activity.userId || '',
      action: activity.action || '',
      ...activity,
    };
    await database.put('activities', newActivity);
    return newActivity;
  },
};

// Safety Form Service (PTP)
export const localSafetyFormService = {
  async getAll(): Promise<LocalSafetyForm[]> {
    const database = await initLocalDB();
    return await database.getAll('safetyForms');
  },

  async getById(id: string): Promise<LocalSafetyForm | undefined> {
    const database = await initLocalDB();
    return await database.get('safetyForms', id);
  },

  async create(form: Partial<LocalSafetyForm>): Promise<LocalSafetyForm> {
    const database = await initLocalDB();
    const newForm: LocalSafetyForm = {
      id: crypto.randomUUID(),
      formType: form.formType || 'PTP',
      requiredPermits: form.requiredPermits || [],
      requiredChecklist: form.requiredChecklist || [],
      ppe: form.ppe || '',
      hazards: form.hazards || [],
      controlMeasures: form.controlMeasures || [],
      tasks: form.tasks || [],
      emergencyActionPlan: form.emergencyActionPlan || false,
      emergencyMusterArea: form.emergencyMusterArea || '',
      emergencyContacts: form.emergencyContacts || '',
      teamMembers: form.teamMembers || [],
      status: form.status || 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...form,
    };
    await database.put('safetyForms', newForm);
    return newForm;
  },

  async update(id: string, updates: Partial<LocalSafetyForm>): Promise<LocalSafetyForm> {
    const database = await initLocalDB();
    const existing = await database.get('safetyForms', id);
    if (!existing) throw new Error('Safety form not found');
    const updated: LocalSafetyForm = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    await database.put('safetyForms', updated);
    return updated;
  },

  async delete(id: string): Promise<void> {
    const database = await initLocalDB();
    await database.delete('safetyForms', id);
  },
};

